import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Box,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip,
  LinearProgress,
  Badge,
  Stack,
  CardActionArea,
  Fade,
  Grow,
  Zoom
} from '@mui/material';
import { 
  VideoCall, 
  Schedule, 
  Person, 
  CalendarToday,
  Timer,
  Work,
  Refresh,
  Assignment,
  CheckCircle,
  Warning,
  TrendingUp,
  EmojiEvents,
  PlayArrow,
  Visibility,
  Notifications,
  Star,
  BusinessCenter,
  LocationOn,
  AccessTime
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isCandidate, isAdmin, isRecruiter } = useAuth();
  const [loading, setLoading] = useState(false);
  const [todaysInterviews, setTodaysInterviews] = useState([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [candidateStats, setCandidateStats] = useState({
    activeApplications: 0,
    pendingTasks: 0,
    interviews: 0,
    offers: 0
  });
  const [applications, setApplications] = useState([]);
  const [pendingAssessments, setPendingAssessments] = useState([]);
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalApplications: 0,
    pendingReviews: 0,
    scheduledInterviews: 0
  });

  useEffect(() => {
    // Redirect administrators to the admin landing page
    if (isAdmin || isRecruiter) {
      navigate('/admin-landing');
      return;
    }
    fetchDashboardData();
  }, [isAdmin, isRecruiter, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (isCandidate) {
        try {
          // Fetch candidate's applications and stats
          const applicationsRes = await axios.get('http://localhost:5000/api/applications/my-applications', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const applications = applicationsRes.data.data || [];
          setApplications(applications);
          
          // Calculate stats
          const activeApplications = applications.filter(app => 
            app.status !== 'rejected' && app.status !== 'withdrawn'
          ).length;
          
          const pendingTasks = applications.filter(app => 
            app.currentStage === 'Technical Assessment'
          ).length;
          
          const interviews = applications.filter(app => 
            app.status === 'interview_scheduled' || app.currentStage === 'Interview'
          ).length;
          
          const offers = applications.filter(app => 
            app.currentStage === 'Offer' || app.status === 'hired'
          ).length;
          
          setCandidateStats({
            activeApplications,
            pendingTasks,
            interviews,
            offers
          });

          // Get pending assessments
          const assessmentApplications = applications.filter(app => 
            app.currentStage === 'Technical Assessment'
          );
          setPendingAssessments(assessmentApplications);
        } catch (err) {
          console.error('Error fetching candidate data:', err);
        }
      }
      
      if (isAdmin || isRecruiter) {
        try {
          // Fetch interviews
          const [todaysRes, upcomingRes, statsRes] = await Promise.all([
            axios.get('http://localhost:5000/api/interviews?today=true', {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get('http://localhost:5000/api/interviews?upcoming=true', {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get('http://localhost:5000/api/interviews/stats/overview', {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          
          setTodaysInterviews(todaysRes.data.data || []);
          setUpcomingInterviews(upcomingRes.data.data || []);
          setStats({
            ...stats,
            scheduledInterviews: statsRes.data.data.upcomingCount || 0
          });
        } catch (err) {
          console.error('Error fetching interview data:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getInterviewTypeColor = (type) => {
    const colors = {
      'technical': 'primary',
      'hr': 'secondary',
      'behavioral': 'info',
      'cultural': 'warning',
      'final': 'success',
      'screening': 'default'
    };
    return colors[type] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': 'info',
      'under_review': 'warning',
      'shortlisted': 'success',
      'interview_scheduled': 'primary',
      'rejected': 'error',
      'hired': 'success',
      'withdrawn': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'submitted': 'Submitted',
      'under_review': 'Under Review',
      'shortlisted': 'Shortlisted',
      'interview_scheduled': 'Interview Scheduled',
      'rejected': 'Rejected',
      'hired': 'Hired',
      'withdrawn': 'Withdrawn'
    };
    return labels[status] || status;
  };

  const calculateDaysSince = (date) => {
    const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1 }}>
            Welcome back, {user?.profile?.firstName || user?.email}! 👋
          </Typography>
          <Typography variant="h6" color="textSecondary">
            Here's what's happening with your applications
          </Typography>
        </Box>
        <IconButton 
          onClick={fetchDashboardData} 
          color="primary"
          sx={{ 
            p: 2, 
            bgcolor: 'primary.main', 
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          <Refresh />
        </IconButton>
      </Box>
      
      {isCandidate && (
        <>
          {/* Profile Completion Alert */}
          {(!user?.profile?.firstName || 
            !user?.profile?.lastName || 
            !user?.profile?.phone ||
            !user?.profile?.address?.city ||
            !user?.profile?.address?.state ||
            !user?.profile?.resume) && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  color="inherit" 
                  size="small"
                  variant="contained"
                  onClick={() => navigate('/profile')}
                >
                  Complete Profile
                </Button>
              }
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Your profile is incomplete!
              </Typography>
              <Typography variant="body2">
                Please update your profile including your resume, city and state to apply for jobs and access all features.
              </Typography>
            </Alert>
          )}

          {/* Pending Assessments Alert */}
          {pendingAssessments.length > 0 && (
            <Alert 
              severity="info" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  color="inherit" 
                  size="small"
                  variant="contained"
                  startIcon={<Assignment />}
                  onClick={() => navigate('/my-applications')}
                >
                  View Assessments
                </Button>
              }
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                You have {pendingAssessments.length} pending assessment{pendingAssessments.length > 1 ? 's' : ''}!
              </Typography>
              <Typography variant="body2">
                Complete your technical assessments to move forward in the hiring process.
              </Typography>
            </Alert>
          )}
          
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={300}>
                <Card sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Work sx={{ fontSize: 40, mr: 2 }} />
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                          {candidateStats.activeApplications}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Active Applications
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={400}>
                <Card sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Assignment sx={{ fontSize: 40, mr: 2 }} />
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                          {candidateStats.pendingTasks}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Pending Tasks
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={500}>
                <Card sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <VideoCall sx={{ fontSize: 40, mr: 2 }} />
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                          {candidateStats.interviews}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Interviews
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Grow in timeout={600}>
                <Card sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  color: 'white',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-4px)' }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <EmojiEvents sx={{ fontSize: 40, mr: 2 }} />
                      <Box>
                        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                          {candidateStats.offers}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Offers
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grid>

          {/* Recent Applications */}
          {applications.length > 0 && (
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                  Recent Applications
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/my-applications')}
                  startIcon={<Visibility />}
                >
                  View All
                </Button>
              </Box>
              <Grid container spacing={2}>
                {applications.slice(0, 3).map((application, index) => (
                  <Grid item xs={12} md={4} key={application._id}>
                    <Fade in timeout={700 + index * 200}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          border: application.currentStage === 'Technical Assessment' ? 2 : 0,
                          borderColor: 'primary.main',
                          '&:hover': { 
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                          }
                        }}
                        onClick={() => navigate('/my-applications')}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {application.job?.title || 'Position'}
                            </Typography>
                            <Chip
                              label={getStatusLabel(application.status)}
                              color={getStatusColor(application.status)}
                              size="small"
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <BusinessCenter sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="textSecondary">
                              {application.job?.department || 'Department'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <LocationOn sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="textSecondary">
                              {application.job?.location || 'Location'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="textSecondary">
                              Applied {calculateDaysSince(application.createdAt)}
                            </Typography>
                            {application.currentStage === 'Technical Assessment' && (
                              <Chip
                                icon={<Assignment />}
                                label="Assessment"
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          {/* Progress Bar */}
                          <Box sx={{ mt: 2 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(() => {
                                const stages = ['Applied', 'Screening', 'Technical Assessment', 'Interview', 'Offer', 'Hired'];
                                const currentIndex = stages.indexOf(application.currentStage);
                                return ((currentIndex + 1) / stages.length) * 100;
                              })()}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                              {application.currentStage}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Quick Actions */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <CardActionArea onClick={() => navigate('/jobs')}>
                  <Card sx={{ p: 2, textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)' } }}>
                    <Work sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Browse Jobs
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Find new opportunities
                    </Typography>
                  </Card>
                </CardActionArea>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardActionArea onClick={() => navigate('/my-applications')}>
                  <Card sx={{ p: 2, textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)' } }}>
                    <Assignment sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      My Applications
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Track your progress
                    </Typography>
                  </Card>
                </CardActionArea>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardActionArea onClick={() => navigate('/profile')}>
                  <Card sx={{ p: 2, textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)' } }}>
                    <Person sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Update Profile
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Keep it current
                    </Typography>
                  </Card>
                </CardActionArea>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardActionArea onClick={() => navigate('/tasks')}>
                  <Card sx={{ p: 2, textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)' } }}>
                    <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Task Submissions
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      View your work
                    </Typography>
                  </Card>
                </CardActionArea>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      {(isAdmin || isRecruiter) && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Active Jobs
                  </Typography>
                  <Typography variant="h3">{stats.activeJobs}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Applications
                  </Typography>
                  <Typography variant="h3">{stats.totalApplications}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Reviews
                  </Typography>
                  <Typography variant="h3">{stats.pendingReviews}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Scheduled Interviews
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {stats.scheduledInterviews}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Today's Interviews */}
          {todaysInterviews.length > 0 && (
            <Paper sx={{ mt: 3, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6">Today's Interviews</Typography>
                <Chip 
                  label={`${todaysInterviews.length} scheduled`} 
                  color="primary" 
                  size="small" 
                  sx={{ ml: 2 }}
                />
              </Box>
              <List>
                {todaysInterviews.map((interview, index) => (
                  <React.Fragment key={interview._id}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <Avatar sx={{ mr: 2 }}>
                        <Person />
                      </Avatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1">
                                {interview.candidate?.profile?.firstName && interview.candidate?.profile?.lastName 
                                  ? `${interview.candidate.profile.firstName} ${interview.candidate.profile.lastName}`
                                  : 'Candidate'}
                              </Typography>
                              <Chip 
                                label={interview.interviewType} 
                                size="small"
                                color={getInterviewTypeColor(interview.interviewType)}
                              />
                            </Box>
                            {interview.candidate?.email && (
                              <Typography variant="body2" color="text.secondary">
                                {interview.candidate.email}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" component="span">
                              <Timer sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                              {formatTime(interview.scheduledDate)}
                            </Typography>
                           <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                             {interview.duration} minutes
                           </Typography>
                           {interview.job && (
                             <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                               {interview.job.title}
                             </Typography>
                           )}
                         </Box>
                       }
                     />
                     <ListItemSecondaryAction>
                       <Tooltip title="Open Google Meet">
                         <IconButton
                           edge="end"
                           color="primary"
                           onClick={() => window.open(interview.meetingLink, '_blank')}
                         >
                           <VideoCall />
                         </IconButton>
                       </Tooltip>
                     </ListItemSecondaryAction>
                   </ListItem>
                 </React.Fragment>
               ))}
             </List>
           </Paper>
         )}

         {/* Upcoming Interviews */}
         <Paper sx={{ mt: 3, p: 3 }}>
           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
             <Schedule sx={{ mr: 1 }} color="primary" />
             <Typography variant="h6">Upcoming Interviews</Typography>
             {upcomingInterviews.length > 0 && (
               <Chip 
                 label={`${upcomingInterviews.length} scheduled`} 
                 size="small" 
                 sx={{ ml: 2 }}
               />
             )}
           </Box>
           
           {upcomingInterviews.length > 0 ? (
             <List>
               {upcomingInterviews.slice(0, 5).map((interview, index) => (
                 <React.Fragment key={interview._id}>
                   {index > 0 && <Divider />}
                   <ListItem>
                     <Avatar sx={{ mr: 2 }}>
                       <Person />
                     </Avatar>
                     <ListItemText
                       primary={
                         <Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <Typography variant="body1">
                               {interview.candidate?.profile?.firstName && interview.candidate?.profile?.lastName 
                                 ? `${interview.candidate.profile.firstName} ${interview.candidate.profile.lastName}`
                                 : 'Candidate'}
                             </Typography>
                             <Chip 
                               label={interview.interviewType} 
                               size="small"
                               color={getInterviewTypeColor(interview.interviewType)}
                             />
                           </Box>
                           {interview.candidate?.email && (
                             <Typography variant="body2" color="text.secondary">
                               {interview.candidate.email}
                             </Typography>
                           )}
                         </Box>
                       }
                       secondary={
                         <Box sx={{ mt: 1 }}>
                           <Typography variant="body2">
                             <CalendarToday sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                             {formatDate(interview.scheduledDate)} at {formatTime(interview.scheduledDate)}
                           </Typography>
                           {interview.job && (
                             <Typography variant="body2">
                               <Work sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                               {interview.job.title}
                             </Typography>
                           )}
                         </Box>
                       }
                     />
                     <ListItemSecondaryAction>
                       <Tooltip title="Open Google Meet">
                         <IconButton
                           edge="end"
                           color="primary"
                           onClick={() => window.open(interview.meetingLink, '_blank')}
                         >
                           <VideoCall />
                         </IconButton>
                       </Tooltip>
                     </ListItemSecondaryAction>
                   </ListItem>
                 </React.Fragment>
               ))}
             </List>
           ) : (
             <Box sx={{ p: 3, textAlign: 'center' }}>
               <Typography variant="body1" color="text.secondary">
                 No upcoming interviews scheduled
               </Typography>
               <Button
                 variant="outlined"
                 sx={{ mt: 2 }}
                 onClick={() => navigate('/task-submissions')}
               >
                 Review Task Submissions
               </Button>
             </Box>
           )}
         </Paper>
       </>
     )}
   </Container>
 );
};

export default Dashboard;
